import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RiSendPlaneFill, RiAddLine, RiEdit2Line, RiCloseLine } from '@remixicon/react';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import { apiClient, createQuoteWithRetry, freshQuoteNumber } from '@/lib/api';
import { EmptyState2Gray } from '@assets/images';
import { Drawer, DrawerContent, DrawerClose } from '@components/ui/Drawer';
import { buildInvoiceEmailHtml } from '@/utils/emailTemplates';
import { usePlatformFeeRate } from '@/hooks/usePlatformFeeRate';
import { formatPlatformFeePercent } from '@/utils/platformFee';
import { trunc2 } from '@/utils';

const SERVICE_COST_RATE = 0.10;

const formatWithCommas = (val) => {
  const stripped = String(val).replace(/,/g, '').replace(/[^\d.]/g, '');
  const parts = stripped.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.slice(0, 2).join(stripped.includes('.') ? '.' : '');
};

const parseAmount = (val) => parseFloat(String(val || '').replace(/,/g, '')) || 0;

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AddServiceDrawer = ({ isOpen, onClose, onSave, initialName = '', initialCost = '' }) => {
  const [name, setName] = useState(initialName);
  const [cost, setCost] = useState(initialCost ? fmt(initialCost) : '');

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setCost(initialCost ? fmt(initialCost) : '');
    }
  }, [isOpen, initialName, initialCost]);

  const parsedCost = parseAmount(cost);
  const isValid = name.trim() && parsedCost > 0;

  const handleSave = () => {
    if (!isValid) return;
    onSave({ name: name.trim(), baseCost: parsedCost });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DrawerContent side="right" hideCloseButton className="w-full max-w-md lg:max-w-2xl p-0 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5">
          <h2 className="text-xl font-bold text-primary-700">Add Service</h2>
          <DrawerClose asChild>
            <button
              type="button"
              onClick={handleClose}
              className="p-1 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <RiCloseLine className="w-4 h-4" />
            </button>
          </DrawerClose>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Service Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Service Details <span className="text-primary-500">*</span>
            </label>
            <textarea
              rows={4}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Ground transportation"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Service Cost <span className="text-primary-500">*</span>
            </label>
            <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent bg-white">
              <span className="pl-3 pr-2 text-sm text-gray-400 font-medium select-none">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={cost}
                onChange={(e) => setCost(formatWithCommas(e.target.value))}
                placeholder="0.00"
                className="flex-1 py-2.5 pr-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none bg-transparent"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-6 pt-3 border-t border-gray-100 shrink-0">
          <button
            type="button"
            disabled={!isValid}
            onClick={handleSave}
            className={`w-full flex items-center justify-center gap-1.5 py-3.5 rounded-full text-sm font-medium transition-colors ${
              isValid ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <RiAddLine className="w-4 h-4" />
            Add Service
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const AdditionalServicesInvoicePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setPageHeaderOverride, clearPageHeaderOverride } = useAppStore();
  const { userProfile } = useAuthStore();

  const quoteNumber = location.state?.quoteNumber;
  const tripLabel = location.state?.tripLabel || '';
  const clientId = location.state?.clientId || '';
  const clientEmail = location.state?.clientEmail;
  const clientName = location.state?.clientName;
  const quoteId = location.state?.quoteId;
  const flightId = location.state?.flightId;

  const [services, setServices] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [taxPercent, setTaxPercent] = useState(SERVICE_COST_RATE * 100);

  // Platform fee — bracket-aware. Resolves against the invoice's
  // totalCost (below), so an org with tiered brackets picks the right
  // rate for THIS invoice's dollar band; falls through to the flat
  // organization.platform_fee_percent, then 1.5%.
  const { resolveForSubtotal } = usePlatformFeeRate();

  useEffect(() => {
    setPageHeaderOverride({
      title: 'Additional Services Invoice',
      subtitle: tripLabel || undefined,
    });
    return () => clearPageHeaderOverride();
  }, [tripLabel, setPageHeaderOverride, clearPageHeaderOverride]);

  const hasFlight = !!flightId;
  // Subtotal is pre-tax: sum of each service's base cost (what the client sees
  // as the item amount). Tax and platform fee are added separately below.
  const totalCost = trunc2(services.reduce((sum, s) => sum + (s.baseCost || 0), 0));
  const subTotal = parseFloat(totalCost);
  // Resolved for this invoice's subtotal — reruns whenever a service is
  // added / edited / removed so the label + amount reflect the tier the
  // invoice currently lands in.
  const { percent: feePercentEffective, rate: feeRateEffective } = resolveForSubtotal(subTotal);
  const taxRate = (parseFloat(taxPercent) || 0) / 100;
  const totalTaxes = trunc2(subTotal * taxRate);
  const totalFee = trunc2((subTotal + totalTaxes) * feeRateEffective);
  const totalDue = trunc2(subTotal + totalFee + totalTaxes);
  const canSend = hasFlight && services.length > 0 && !isSending;

  const openAdd = () => {
    setEditIndex(null);
    setIsDrawerOpen(true);
  };

  const openEdit = (index) => {
    setEditIndex(index);
    setIsDrawerOpen(true);
  };

  const handleSaveService = (service) => {
    if (editIndex !== null) {
      setServices((prev) => prev.map((s, i) => (i === editIndex ? service : s)));
    } else {
      setServices((prev) => [...prev, service]);
    }
    setIsDrawerOpen(false);
    setEditIndex(null);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditIndex(null);
  };

  const handleSend = async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      const invoiceItems = services.map((s) => {
        const itemTax = trunc2((s.baseCost || 0) * taxRate);
        return { item: s.name, amount: s.baseCost, tax_amount: itemTax, line_total: (s.baseCost || 0) + itemTax };
      });
      const baseSubtotal = services.reduce((sum, s) => sum + (s.baseCost || 0), 0);
      const invoice = await apiClient.createInvoice({
        broker_org_id: userProfile?.org_id || null,
        created_by: userProfile?.userprofile_id || null,
        client_id: clientId,
        quote_id:  quoteId,
        flight_id: flightId,
        invoice_type: 'ADDITIONAL',
        subtotal: baseSubtotal,
        tax_amount: totalTaxes,
        platform_fee_percent: feePercentEffective,
        platform_fee: totalFee,
        total_amount: totalDue,
        items: invoiceItems,
        status: 'SENT',
      });

      queryClient.invalidateQueries({ queryKey: ['quotes'] });

      if(clientEmail)
      {
        // New checkout token tied to the (possibly new) total amount.
        const requestToken = await apiClient.createCheckoutToken({
          quote_id: invoice.invoice_id,
          amount: totalDue || 0,
          currency: 'USD',
          payment_method: 'CREDIT_CARD',
          description: 'Charter flight quote payment',
          metadata: {
            customer_name: [
              clientName
            ].filter(Boolean).join(' '),
            customer_email: clientEmail,
          },
        });

        const checkoutLink = `${window.location.origin}/checkout?invoiceid=${invoice.invoice_id}&token=${requestToken?.token}&tn=${quoteNumber}&option=invoiceaddservice`;

        //client email send invoice notification
        const emailHtml = buildInvoiceEmailHtml({
          clientName,
          trustjetFee: totalFee,
          trustjetFeePercent: feePercentEffective,
          feeRate: feeRateEffective,
          itemCost: totalCost,
          total: totalDue,
          taxAmount: totalTaxes,
          quoteNumber,
          services: invoiceItems,
          oneOff: false,
          checkoutUrl: checkoutLink
        });

        await apiClient.sendMessage({
          From: 'tools@trustjet.ai',
          To: clientEmail,
          Cc: userProfile?.email || '',
          ReplyTo: userProfile?.email || '',
          Subject: `TrustJet - Additional Service Invoice (${quoteNumber})`,
          HtmlBody: emailHtml,
        });
      }

      toast.success('Additional invoice sent to client.');
      // Reset local state before navigating so any lingering render (or a
      // quick re-mount if the user comes back) doesn't drive the totals
      // off the previous invoice's services + tax rate.
      setServices([]);
      setTaxPercent(SERVICE_COST_RATE * 100);
      setEditIndex(null);
      setIsDrawerOpen(false);
      navigate(-1);
    } catch (err) {
      console.error('Failed to send invoice:', err);
      toast.error('Failed to send invoice');
    } finally {
      setIsSending(false);
    }
  };

  const editService = editIndex !== null ? services[editIndex] : null;

  return (
    <div className="pb-24 w-full lg:max-w-[1000px] bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
      <main className="py-3 flex flex-col" style={{ height: 'calc(108vh - 220px)' }}>

        {!hasFlight && (
          <div className="mb-3 px-4 py-3 rounded-xl bg-amberlight border border-amber-200">
            <p className="text-sm text-amberdark">
              A flight record is required before you can add services to this invoice.
            </p>
          </div>
        )}

        {/* Services area — scrollable */}
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
          {services.length === 0 ? (
            /* Empty state */
            <div className="flex-1 flex flex-col items-center justify-center gap-4 rounded-3xl">
              <img src={EmptyState2Gray} alt="" className="w-24 h-24 opacity-80" />
              <p className="text-sm text-gray-600">No services added yet</p>
              <button
                type="button"
                onClick={openAdd}
                disabled={!hasFlight || isSending}
                className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-full border text-sm transition-colors mt-3 ${
                  hasFlight && !isSending
                    ? 'border-primary-700 text-primary-700'
                    : 'border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <RiAddLine className="w-4 h-4" />
                Add Service
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Service rows */}
              {services.map((service, i) => (
                <div key={i} className="flex items-center justify-between bg-white rounded-2xl border border-gray-100 px-4 py-3.5">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{service.name}</p>
                    <p className="text-sm text-gray-500 mt-0.5">${fmt(service.baseCost)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => openEdit(i)}
                    disabled={!hasFlight || isSending}
                    className={`p-1.5 transition-colors ${hasFlight && !isSending ? 'text-gray-600' : 'text-gray-300 cursor-not-allowed'}`}
                  >
                    <RiEdit2Line className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {/* Add another service */}
              <button
                type="button"
                onClick={openAdd}
                disabled={!hasFlight || isSending}
                className={`w-full flex items-center justify-center gap-1.5 py-3 rounded-2xl border text-sm transition-colors mt-1 ${
                  hasFlight && !isSending
                    ? 'border-primary-700 text-primary-700'
                    : 'border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <RiAddLine className="w-4 h-4" />
                Add Service
              </button>
            </div>
          )}
        </div>

        {/* Totals + Send Invoice — pinned to bottom */}
        <div className="shrink-0 mt-4 space-y-3">
          {services.length > 0 && (
            <div className="space-y-2 px-1">
              <div className="flex justify-between text-sm text-gray-500">
                <span>Subtotal</span>
                <span>${fmt(subTotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>TrustJet Fee ({formatPlatformFeePercent(feePercentEffective)}%)</span>
                <span>${fmt(totalFee)}</span>
              </div>
              <div className="flex justify-between items-center text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  Taxes (
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={taxPercent}
                    onChange={(e) => setTaxPercent(e.target.value)}
                    disabled={isSending}
                    className="w-14 px-1.5 py-0.5 text-sm text-right border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  %)
                </span>
                <span>${fmt(totalTaxes)}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold text-gray-900 border-t border-gray-100 pt-2">
                <span>Total Services Cost</span>
                <span>${fmt(totalDue)}</span>
              </div>
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={!canSend}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-full text-sm transition-all ${
              canSend ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSending ? (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <RiSendPlaneFill className="w-4 h-4" />
            )}
            Send Invoice
          </button>
        </div>
      </main>

      <AddServiceDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        onSave={handleSaveService}
        initialName={editService?.name ?? ''}
        initialCost={editService?.baseCost ?? ''}
      />
    </div>
  );
};

export default AdditionalServicesInvoicePage;
